import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import AddRounded from "@mui/icons-material/AddRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import SearchRounded from "@mui/icons-material/Search";
import { EmptyState, PageHeader, PageSkeleton } from "../components";
import CustomSnackbar, {
  CustomSnackbarRef,
} from "../components/CustomSnackbar";
import { useMedia } from "../context/MediaContext";
import {
  createPaymentMethod,
  getPaymentMethods,
  PaymentMethodItem,
  PaymentMethodPayload,
  reloadPaymentMethods,
  updatePaymentMethod,
} from "../utils/api";

/** Тот же шаблон, что проверяет бэкенд: расхождение дало бы 400 после отправки. */
const CODE_PATTERN = /^[a-z][a-z0-9_]{2,63}$/;

const errorText = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail[0]?.msg ?? fallback;
  return detail.message ?? fallback;
};

type FormState = {
  code: string;
  currency: string;
  payment_form: boolean;
  card_form: boolean;
  page_code: string;
  phoneEnabled: boolean;
  country_code: string;
  national_len: string;
  local_prefixes: string;
  bank_aliases: string;
};

const EMPTY_FORM: FormState = {
  code: "",
  currency: "",
  payment_form: false,
  card_form: false,
  page_code: "",
  phoneEnabled: false,
  country_code: "",
  national_len: "",
  local_prefixes: "",
  bank_aliases: "",
};

/** Написания банков редактируются построчно: «написание = каноническое имя». */
const aliasesToText = (aliases: Record<string, string>) =>
  Object.entries(aliases)
    .map(([key, value]) => `${key} = ${value}`)
    .join("\n");

const parseAliases = (text: string): Record<string, string> => {
  const result: Record<string, string> = {};
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const separator = line.indexOf("=");
      if (separator === -1) return;
      const key = line.slice(0, separator).trim();
      const value = line.slice(separator + 1).trim();
      if (key && value) result[key] = value;
    });
  return result;
};

const toForm = (item: PaymentMethodItem): FormState => ({
  code: item.code,
  currency: item.currency ?? "",
  payment_form: item.payment_form,
  card_form: item.card_form,
  page_code: item.page_code ?? "",
  phoneEnabled: Boolean(item.phone),
  country_code: item.phone?.country_code ?? "",
  national_len: item.phone ? String(item.phone.national_len) : "",
  local_prefixes: item.phone?.local_prefixes.join(", ") ?? "",
  bank_aliases: aliasesToText(item.bank_aliases ?? {}),
});

const PaymentMethodsPage = () => {
  const theme = useTheme();
  const { isMobile600 } = useMedia();
  const snackbarRef = useRef<CustomSnackbarRef>(null);

  const [items, setItems] = useState<PaymentMethodItem[]>([]);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PaymentMethodItem | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPaymentMethods();
      setItems(res.data?.items ?? []);
      setCurrencies(res.data?.currencies ?? []);
      setFailed(false);
    } catch (err) {
      setFailed(true);
      snackbarRef.current?.showSnackbar(
        errorText(err, "Не удалось загрузить методы"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter(
      (item) =>
        item.code.toLowerCase().includes(query) ||
        (item.currency ?? "").toLowerCase().includes(query),
    );
  }, [items, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setFormOpen(true);
  };

  // PUT замещает настройки целиком, поэтому форма заполняется текущими
  // значениями: иначе правка одного поля стёрла бы остальные.
  const openEdit = (item: PaymentMethodItem) => {
    setEditing(item);
    setForm(toForm(item));
    setFormError(null);
    setFormOpen(true);
  };

  const submit = async () => {
    const code = form.code.trim();
    if (!editing && !CODE_PATTERN.test(code)) {
      setFormError(
        "Код: строчная латиница, цифры и подчёркивание, начинается с буквы, 3–64 символа",
      );
      return;
    }
    if (form.card_form && !form.payment_form) {
      setFormError("Ввод карты невозможен без нашей платёжной страницы");
      return;
    }

    let phone = null;
    if (form.phoneEnabled) {
      const len = Number(form.national_len);
      if (!form.country_code.trim() || !Number.isInteger(len)) {
        setFormError("Заполните код страны и длину номера");
        return;
      }
      if (len < 4 || len > 15) {
        setFormError("Длина национальной части — от 4 до 15");
        return;
      }
      phone = {
        country_code: form.country_code.trim(),
        national_len: len,
        local_prefixes: form.local_prefixes
          .split(",")
          .map((prefix) => prefix.trim())
          .filter(Boolean),
      };
    }

    const payload: PaymentMethodPayload = {
      currency: form.currency || null,
      payment_form: form.payment_form,
      card_form: form.card_form,
      page_code: form.page_code.trim() || null,
      phone,
      bank_aliases: parseAliases(form.bank_aliases),
    };

    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        await updatePaymentMethod(editing.code, payload);
        snackbarRef.current?.showSnackbar("Метод обновлён", "success");
      } else {
        await createPaymentMethod(code, payload);
        snackbarRef.current?.showSnackbar("Метод заведён", "success");
      }
      setFormOpen(false);
      load();
    } catch (err) {
      setFormError(errorText(err, "Не удалось сохранить"));
    } finally {
      setSaving(false);
    }
  };

  const reload = async () => {
    try {
      const res = await reloadPaymentMethods();
      snackbarRef.current?.showSnackbar(
        `Перечитано методов: ${res.data?.loaded ?? 0}`,
        "success",
      );
      load();
    } catch (err) {
      snackbarRef.current?.showSnackbar(
        errorText(err, "Не удалось перечитать"),
        "error",
      );
    }
  };

  const sourceChip = (item: PaymentMethodItem) => (
    <Chip
      size="small"
      label={item.source}
      variant={item.source === "база" ? "filled" : "outlined"}
      color={item.source === "база" ? "primary" : "default"}
      sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
    />
  );

  const formChips = (item: PaymentMethodItem) => (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {item.payment_form && (
        <Chip size="small" label="страница" sx={{ height: 20, fontSize: 11 }} />
      )}
      {item.card_form && (
        <Chip size="small" label="карта" sx={{ height: 20, fontSize: 11 }} />
      )}
      {item.phone && (
        <Chip
          size="small"
          label={`+${item.phone.country_code} · ${item.phone.national_len}`}
          sx={{ height: 20, fontSize: 11 }}
        />
      )}
      {Object.keys(item.bank_aliases ?? {}).length > 0 && (
        <Chip
          size="small"
          label={`банков: ${Object.keys(item.bank_aliases).length}`}
          sx={{ height: 20, fontSize: 11 }}
        />
      )}
    </Stack>
  );

  const renderContent = () => {
    if (loading) return <PageSkeleton />;

    if (failed) {
      return (
        <EmptyState
          icon={AccountBalanceWalletOutlined}
          title="Реестр недоступен"
          description="Не удалось получить методы. Раздел доступен только администратору."
          action={
            <Button variant="contained" onClick={load}>
              Повторить
            </Button>
          }
        />
      );
    }

    if (!shown.length) {
      return (
        <EmptyState
          icon={AccountBalanceWalletOutlined}
          title="Методов не найдено"
          description={
            search ? "По запросу ничего нет." : "Заведите первый метод оплаты."
          }
          action={
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={openCreate}
            >
              Добавить
            </Button>
          }
        />
      );
    }

    if (isMobile600) {
      return (
        <Stack spacing={1.5}>
          {shown.map((item) => (
            <Paper
              key={item.code}
              variant="outlined"
              sx={{ p: 2, borderRadius: "16px" }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1}
              >
                <Box minWidth={0}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      wordBreak: "break-all",
                      fontFamily: "monospace",
                    }}
                  >
                    {item.code}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ mt: 0.75 }}
                    alignItems="center"
                  >
                    <Chip
                      size="small"
                      label={
                        item.currency
                          ? item.currency.toUpperCase()
                          : "без валюты"
                      }
                      color={item.currency ? "default" : "warning"}
                      sx={{ height: 20, fontSize: 11 }}
                    />
                    {sourceChip(item)}
                  </Stack>
                </Box>
                <IconButton
                  size="small"
                  aria-label={`Изменить ${item.code}`}
                  onClick={() => openEdit(item)}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
              </Stack>
              <Divider sx={{ my: 1.5 }} />
              {formChips(item)}
            </Paper>
          ))}
        </Stack>
      );
    }

    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: "16px" }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.table.head }}>
              <TableCell sx={{ fontWeight: 700 }}>Код</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Валюта</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Настройки</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Экран</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Источник</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Действия
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((item) => (
              <TableRow key={item.code} hover>
                <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                  {item.code}
                </TableCell>
                <TableCell>
                  {item.currency ? (
                    item.currency.toUpperCase()
                  ) : (
                    <Tooltip title="Без валюты заказ по методу создать нельзя">
                      <Chip
                        size="small"
                        color="warning"
                        label="не задана"
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{formChips(item)}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {item.page_code ?? "—"}
                </TableCell>
                <TableCell>{sourceChip(item)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Изменить">
                    <IconButton
                      size="small"
                      aria-label={`Изменить ${item.code}`}
                      onClick={() => openEdit(item)}
                    >
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: {
          xs: "calc(var(--content-max-height) + 32.5dvh)",
          md: "82vh",
        },
        overflow: "auto",
        // Тот же боковой отступ, что у остальных разделов панели.
        p: { xs: 2, sm: 3 },
      }}
    >
      <PageHeader
        icon={AccountBalanceWalletOutlined}
        title="Методы оплаты"
        description="Реестр методов: валюта, платёжная форма, правила номера и банков"
        actions={
          <>
            <Tooltip title="Перечитать настройки из базы. Нужно, только если их меняли напрямую в БД">
              <Button
                variant="outlined"
                startIcon={<RefreshRounded />}
                onClick={reload}
              >
                Перечитать
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={openCreate}
            >
              Добавить
            </Button>
          </>
        }
      />

      <TextField
        size="small"
        fullWidth
        placeholder="Поиск по коду или валюте"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRounded fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {renderContent()}

      <Dialog
        open={formOpen}
        onClose={() => !saving && setFormOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? `Метод ${editing.code}` : "Новый метод оплаты"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {!editing && (
              <TextField
                label="Код метода"
                size="small"
                fullWidth
                value={form.code}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, code: e.target.value }))
                }
                helperText="Строчная латиница, цифры и подчёркивание. Изменить потом нельзя."
              />
            )}

            {editing && (
              <Typography variant="caption" color="text.secondary">
                Сохранение замещает настройки целиком — поля заполнены текущими
                значениями, очищенное поле будет сброшено.
              </Typography>
            )}

            <Select
              size="small"
              fullWidth
              displayEmpty
              value={form.currency}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, currency: e.target.value }))
              }
            >
              <MenuItem value="">Валюта не задана</MenuItem>
              {currencies.map((code) => (
                <MenuItem key={code} value={code}>
                  {code.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
            {!form.currency && (
              <Typography variant="caption" color="text.secondary">
                Без валюты заказ по методу создать нельзя.
              </Typography>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={form.payment_form}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      payment_form: e.target.checked,
                      // Ввод карты без нашей страницы бэкенд отклоняет.
                      card_form: e.target.checked ? prev.card_form : false,
                    }))
                  }
                />
              }
              label="Идёт через нашу платёжную страницу"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.card_form}
                  disabled={!form.payment_form}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, card_form: e.target.checked }))
                  }
                />
              }
              label="На странице вводится карта"
            />

            <TextField
              label="Код экрана"
              size="small"
              fullWidth
              value={form.page_code}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, page_code: e.target.value }))
              }
              helperText="Заполняется, только если экран отличается от кода метода"
            />

            <Divider />

            <FormControlLabel
              control={
                <Switch
                  checked={form.phoneEnabled}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      phoneEnabled: e.target.checked,
                    }))
                  }
                />
              }
              label="Правило телефонного номера"
            />
            {form.phoneEnabled && (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  label="Код страны"
                  size="small"
                  fullWidth
                  value={form.country_code}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      country_code: e.target.value,
                    }))
                  }
                />
                <TextField
                  label="Длина номера"
                  size="small"
                  type="number"
                  fullWidth
                  value={form.national_len}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      national_len: e.target.value,
                    }))
                  }
                />
                <TextField
                  label="Местные префиксы"
                  size="small"
                  fullWidth
                  value={form.local_prefixes}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      local_prefixes: e.target.value,
                    }))
                  }
                  helperText="Через запятую"
                />
              </Stack>
            )}

            <TextField
              label="Написания банков"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={form.bank_aliases}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, bank_aliases: e.target.value }))
              }
              helperText="По строке на банк: написание = каноническое имя"
            />

            {formError && (
              <Typography variant="body2" color="error">
                {formError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            Отмена
          </Button>
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Сохранить"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackbar ref={snackbarRef} />
    </Box>
  );
};

export default PaymentMethodsPage;
